import React, { useState } from 'react';
import {
    Box, Typography, TextField, Button,
    Divider, CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
    createUserWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup,
    updateProfile,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { GoogleIcon, FacebookIcon } from '../../components/CustomIcons';
import logo from '../../images/logo.svg';
import { colors, typography, radius, inputSx, btnPrimary } from '../../theme';

const SignUpUser = () => {
    const navigate  = useNavigate();
    const { login } = useAuth();

    const [showPass, setShowPass] = useState(false);
    const [loading,  setLoading]  = useState(false);
    const [error,    setError]    = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data     = new FormData(e.currentTarget);
        const name     = data.get('name');
        const email    = data.get('email');
        const password = data.get('password');

        if (!name || !email || !password) { setError('Please fill in all fields.'); return; }
        if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }

        setError(''); setLoading(true);
        let user = null;
        try {
            const userCred = await createUserWithEmailAndPassword(auth, email, password);
            user = userCred.user;
            await updateProfile(user, { displayName: name });
            await setDoc(doc(db, 'users', user.uid), { name, email, role: 'user', createdAt: serverTimestamp() });
            login(user, 'user');
            navigate('/user/dashboard');
        } catch (err) {
            if (user) { try { await user.delete(); } catch (_) {} }
            setError(err.code === 'auth/email-already-in-use'
                ? 'This email is already registered. Sign in instead.'
                : err.message);
        }
        setLoading(false);
    };

    const handleGoogle = async () => {
        setLoading(true); setError('');
        try {
            const result   = await signInWithPopup(auth, new GoogleAuthProvider());
            const user     = result.user;
            const userSnap = await getDoc(doc(db, 'users', user.uid));
            if (userSnap.exists()) {
                const userData = userSnap.data();
                login(user, userData.role ?? 'user');
                navigate('/user/dashboard');
                return;
            }
            await setDoc(doc(db, 'users', user.uid), { name: user.displayName, email: user.email, role: 'user', createdAt: serverTimestamp() });
            login(user, 'user');
            navigate('/user/dashboard');
        } catch (err) {
            console.error(err);
            setError('Google sign-in failed. Try again.');
        }
        setLoading(false);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: colors.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, py: 4 }}>
            <Box sx={{ width: '100%', maxWidth: 400 }}>

                {/* Back */}
                <Box onClick={() => navigate('/signin')} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: colors.textFaintest, cursor: 'pointer', mb: 2, fontSize: 13, '&:hover': { color: colors.primary } }}>
                    ← Back to sign in
                </Box>

                {/* Logo */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <img src={logo} alt="DigitalQ" style={{ width: 80, height: 'auto' }} />
                </Box>

                <Box sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, p: 3 }}>

                    {/* Role badge — keep customer blue */}
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, bgcolor: colors.bg, border: '1px solid #4a9eff44', borderRadius: radius.md, px: 1.2, py: 0.4, mb: 2 }}>
                        <Typography sx={{ fontSize: 13 }}>👤</Typography>
                        <Typography sx={{ fontSize: 12, color: '#4a9eff', fontWeight: 600 }}>Customer</Typography>
                    </Box>

                    <Typography sx={{ fontFamily: typography.poppins, fontSize: 22, fontWeight: 700, color: colors.textPrimary, mb: 0.5 }}>
                        Create Account
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: colors.textFaint, mb: 3 }}>
                        Sign up to start booking barbers
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField name="name"  label="Full Name" required size="small" sx={inputSx} />
                        <TextField name="email" label="Email"     required size="small" sx={inputSx} type="email" />
                        <TextField name="password" label="Password" required size="small" sx={inputSx}
                            type={showPass ? 'text' : 'password'}
                            InputProps={{ endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPass(s => !s)} edge="end" size="small" sx={{ color: colors.textFaintest }}>
                                        {showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            )}} />

                        {error && <Typography sx={{ fontSize: 12, color: colors.error, textAlign: 'center' }}>{error}</Typography>}

                        <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ ...btnPrimary, fontSize: 14, py: 1.2 }}>
                            {loading ? <CircularProgress size={18} sx={{ color: colors.bgDeep }} /> : 'Create Account'}
                        </Button>

                        <Typography sx={{ textAlign: 'center', fontSize: 13, color: colors.textFaint }}>
                            Already have an account?{' '}
                            <Typography component="span" onClick={() => navigate('/signin')} sx={{ color: '#4a9eff', cursor: 'pointer', fontSize: 13, '&:hover': { textDecoration: 'underline' } }}>
                                Sign in
                            </Typography>
                        </Typography>
                    </Box>

                    <Divider sx={{ borderColor: colors.border, my: 2.5 }}>
                        <Typography sx={{ fontSize: 12, color: colors.textFaintest, px: 1 }}>or</Typography>
                    </Divider>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Button fullWidth variant="outlined" onClick={handleGoogle} disabled={loading} startIcon={<GoogleIcon />}
                            sx={{ borderColor: colors.border, color: colors.textPrimary, textTransform: 'none', fontSize: 13, borderRadius: radius.md, py: 1, '&:hover': { borderColor: colors.primaryBorder, bgcolor: colors.bg } }}>
                            Continue with Google
                        </Button>
                        <Button fullWidth variant="outlined" disabled startIcon={<FacebookIcon />}
                            sx={{ borderColor: colors.border, color: colors.textPrimary, textTransform: 'none', fontSize: 13, borderRadius: radius.md, py: 1 }}>
                            Facebook (Coming Soon)
                        </Button>
                    </Box>

                    <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${colors.border}`, textAlign: 'center' }}>
                        <Typography onClick={() => navigate('/')} sx={{ fontSize: 12, color: colors.textFaintest, cursor: 'pointer', '&:hover': { color: colors.primary } }}>
                            ← Not a customer? Choose a different role
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default SignUpUser;